import { CircularProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "../api/axios";
import useAuth from "../hooks/useAuth";
import Consent from "../Pages/consent/consent";
import PromptNoneConsent from "../Pages/consent/promptNoneConsent";
import Register from "../Pages/registration/register";
import SignInWithEmail from "../Pages/signIn/signInWithEmail";

// ConsentRoute decides and returns a consent component based on prompt type.
function ConsentRoute() {
  const [response, setResponse] = useState({});
  const { auth, setAuth, setRedirectUri, setCurrentTenant, setAuthWithTenant } =
    useAuth();

  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  let location = useLocation();

  // Extract tenant_id from URL if provided by the backend
  const tenantId = params.tenantId || params.tenant_id;
  const consentId = params.consentId || params.consent_id;
  const authorizationCode = params.code;
  const enabledAuthMethods = params.enabled_auth_methods?.split(",") || [];
  const botUserName = params.bot_user_name || "";
  const googleRedirect = params.google_redirect || "";
  const appleRedirect = params.apple_redirect || "";
  const telegramRedirect = params.telegram_redirect || "";
  /** Consent URL may include user_action=signin | signup (passed through to /login). */
  const userAction = (
    params.user_action ||
    params.userAction ||
    ""
  )
    .toLowerCase()
    .replace(/[-_]/g, "");

  const parseTenantIdFromIdToken = (idToken) => {
    if (!idToken || typeof idToken !== "string") return null;
    const decodeBase64Url = (value) => {
      const padded = value.padEnd(
        value.length + ((4 - (value.length % 4)) % 4),
        "="
      );
      return atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    };

    const extractTenantIdFromDecoded = (decoded) => {
      // Supports raw JSON and payloads that include a trailing signature blob.
      const jsonStart = decoded.indexOf("{");
      const jsonEnd = decoded.lastIndexOf("}");
      if (jsonStart === -1 || jsonEnd === -1 || jsonEnd < jsonStart) return null;
      const parsed = JSON.parse(decoded.slice(jsonStart, jsonEnd + 1));
      return parsed?.tenant_id || parsed?.tenantId || null;
    };

    try {
      const parts = idToken.split(".");
      if (parts.length < 2) return null;

      const candidatePayloadParts = [];
      if (parts[1] && parts[1] !== "public" && parts[1] !== "local") {
        candidatePayloadParts.push(parts[1]); // JWT style
      }
      if (parts[2]) {
        candidatePayloadParts.push(parts[2]); // PASETO style
      }

      for (const payloadPart of candidatePayloadParts) {
        try {
          const decoded = decodeBase64Url(payloadPart);
          const tenant = extractTenantIdFromDecoded(decoded);
          if (tenant) return tenant;
        } catch {
          // Try next candidate.
        }
      }
      return null;
    } catch {
      return null;
    }
  };

  // Effect to set current tenant from URL params
  useEffect(() => {
    if (tenantId) {
      setCurrentTenant({ id: tenantId });
    }
  }, [tenantId, setCurrentTenant]);

  // UseEffect to get access token with tenant context
  useEffect(() => {
    if (authorizationCode) return;

    // If we already have an access token in memory, use it
    if (auth?.access_token) {
      setResponse({ status: 200, data: { data: auth } });
      return;
    }

    const getAccessToken = async () => {
      try {
        const refreshPath = tenantId ? `/refreshToken?tenant_id=${tenantId}` : "/refreshToken";
        const response = await axios.get(refreshPath, {
          withCredentials: true,
        });
        setResponse(response);
        const authData = response?.data?.data;
        const resolvedTenantId =
          tenantId ||
          authData?.tenant_id ||
          parseTenantIdFromIdToken(authData?.id_token);
        setAuth(authData);
        if (resolvedTenantId) {
          setCurrentTenant({ id: resolvedTenantId });
          setAuthWithTenant(authData, resolvedTenantId);
        }
      } catch (error) {
        setResponse(error.response || { status: 401 });
        setAuth({});
      }
    };
    getAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, auth?.access_token, authorizationCode]);

  // OAuth callback path: if authorization code is present, exchange it first.
  if (authorizationCode) {
    const query = new URLSearchParams();
    query.set("code", authorizationCode);
    if (consentId) query.set("consent_id", consentId);
    if (tenantId) query.set("tenant_id", tenantId);
    return <Navigate to={`/internal_token?${query.toString()}`} replace />;
  }

  // Check if response is ready
  if (response?.status) {
    setRedirectUri(location);
    if (response?.status === 200) {
      if (params.prompt === "none") {
        if (response.data?.data?.access_token)
          return (
            <PromptNoneConsent consentId={consentId} loggedIn={true} />
          );
        else
          return (
            <PromptNoneConsent consentId={consentId} loggedIn={false} />
          );
      } else if (params.prompt === "register") {
        if (response.data?.data?.access_token)
          return <Consent consentId={consentId} />;
        else return <Register consentId={consentId} />;
      } else if (params.prompt === "email") {
        if (response.data?.data?.access_token)
          return <Consent consentId={consentId} />;
        else return <SignInWithEmail consentId={consentId} />;
      } else if (params.prompt === "consent") {
        if (response.data?.data?.access_token)
          return <Consent consentId={consentId} />;
        return (
          <Navigate
            to={`/login${location.search}`}
            replace
            state={{
              from: location,
              enabledAuthMethods,
              botUserName,
              googleRedirect,
              appleRedirect,
              telegramRedirect,
              consentId,
              tenantId,
              userAction,
            }}
          />
        );
      } else {
        if (!response.data?.data?.access_token)
          return (
            <Navigate
              to={`/login?tenant_id=${tenantId || ""}`}
              state={{
                from: location,
                enabledAuthMethods,
                botUserName,
                googleRedirect,
                appleRedirect,
                telegramRedirect,
                consentId,
                tenantId,
                userAction,
              }}
              replace
            />
          );
        else return <Consent consentId={consentId} />;
      }
    } else {
      if (params.prompt === "none")
        return (
          <PromptNoneConsent consentId={consentId} loggedIn={false} />
        );
      else if (params.prompt === "register")
        return <Register consentId={consentId} />;
      else if (params.prompt === "email")
        return <SignInWithEmail consentId={consentId} />;
      else if (params.prompt === "consent") {
        return (
          <Navigate
            to={`/login${location.search}`}
            replace
            state={{
              from: location,
              enabledAuthMethods,
              botUserName,
              googleRedirect,
              appleRedirect,
              telegramRedirect,
              consentId,
              tenantId,
              userAction,
            }}
          />
        );
      } else
        return (
          <Navigate
            to={`/login?tenant_id=${tenantId || ""}`}
            state={{
              from: location,
              enabledAuthMethods,
              botUserName,
              googleRedirect,
              appleRedirect,
              telegramRedirect,
              consentId,
              tenantId,
              userAction,
            }}
            replace
          />
        );
    }
  } else
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          mt: 2,
          minHeight: "100vh",
        }}
      >
        <CircularProgress
          size={40}
          sx={{
            color: "secondary.main",
            marginTop: "-12px",
            marginLeft: "-12px",
            my: 1,
          }}
        />
        <Typography>Please wait...</Typography>
      </Box>
    );
}

export default ConsentRoute;
